import { CampaignStatus } from '@retail/campaign/types';
import { Box, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { CampaignStatusChip } from './CampaignStatusChip';
import { keyframes } from '@mui/system';

interface Props {
  status: CampaignStatus;
}

const ripple = keyframes`
  0% { transform: scale(0.8); opacity: 0.6; }
  70% { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
`;

export const CampaignStatusView = ({ status }: Props) => {
  const theme = useTheme();
  const { status: statusTints } = theme.vars.palette.semantic;

  const statusTint = useMemo(() => {
    switch (status) {
      case 'RELEASED':
        return statusTints.positive;
      case 'UNRELEASED':
      case 'MODIFIED':
        return statusTints.pending;
    }
  }, [statusTints, status]);

  return (
    <Box
      component="span"
      sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <Box
        component="span"
        sx={(theme) => ({
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: statusTint.strong,
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            boxShadow: `0 0 0 0 ${statusTint.strong}`,
            animation: `${ripple} 1.8s ease-out infinite`,
            backgroundColor: statusTint.strong,
            opacity: 0.4,
          },
          ...theme.applyStyles('dark', {
            bgcolor: statusTint.text,
            '&::before': {
              boxShadow: `0 0 0 0 ${statusTint.text}`,
              backgroundColor: statusTint.text,
            },
          }),
        })}
      />
      <Box component="span" sx={{ pl: 2 }}>
        <CampaignStatusChip status={status} longDescription />
      </Box>
    </Box>
  );
};
